import React, {useEffect, useState} from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Container,
  Collapse,
  IconButton,
  Box,
  Paper,
  Tooltip,
  useTheme,
} from '@mui/material';
import {PermissionDto} from '../../../api/dto/permission';
import {getUserMePermission} from '../../../api/user';
import Dashboard from '../../../components/Dashboard';
import {useTranslation} from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';

const AccountPermission = () => {
  const {t} = useTranslation();
  const theme = useTheme();
  const [permissions, setPermissions] = useState<PermissionDto[]>();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const platformId = localStorage.getItem('platformId');

  useEffect(() => {
    getUserMePermission().then(d => {
      const permissionList = d.data.data[platformId ?? ''];
      setPermissions(permissionList);
    });
  }, [platformId]);

  const scopeColors: Record<
    string,
    | 'primary'
    | 'secondary'
    | 'default'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
  > = {
    '*': 'error',
    users: 'info',
    plans: 'success',
    permissions: 'primary',
    roles: 'secondary',
    platforms: 'warning',
  };

  const groupedPermissions = permissions?.reduce((acc, permission) => {
    const scope = permission.scope;
    if (!acc[scope]) {
      acc[scope] = [];
    }
    acc[scope].push(permission);
    return acc;
  }, {} as Record<string, PermissionDto[]>);

  const handleExpandClick = (scope: string) => {
    setExpanded(prevExpanded => ({
      ...prevExpanded,
      [scope]: !prevExpanded[scope],
    }));
  };

  const handleExpandAll = () => {
    const newExpanded = Object.keys(groupedPermissions || {}).reduce(
      (acc, scope) => {
        acc[scope] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    setExpanded(newExpanded);
  };

  const handleCollapseAll = () => {
    const newExpanded = Object.keys(groupedPermissions || {}).reduce(
      (acc, scope) => {
        acc[scope] = false;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    setExpanded(newExpanded);
  };

  return (
    <Container
      id='pricing'
      sx={{
        pt: {xs: 4, sm: 12},
        pb: {xs: 8, sm: 16},
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: {xs: 3, sm: 6},
      }}
    >
      <Card
        variant='outlined'
        sx={{
          maxWidth: 600,
          margin: '20px auto',
          borderRadius: 1.5,
          boxShadow: theme.shadows[1],
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <CardContent sx={{p: 2}}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              pb: 1.5,
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
              <SecurityIcon color='primary' sx={{fontSize: 20}} />
              <Typography variant='h6' component='div' sx={{fontWeight: 500}}>
                {t('dashboardAccountPermission')}
              </Typography>
            </Box>

            <Box sx={{display: 'flex', gap: 0.5}}>
              <Tooltip title={t('expandAll')}>
                <IconButton
                  onClick={handleExpandAll}
                  aria-label='expand all'
                  sx={{
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ExpandMoreIcon sx={{fontSize: 18}} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('collapseAll')}>
                <IconButton
                  onClick={handleCollapseAll}
                  aria-label='collapse all'
                  sx={{
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ExpandLessIcon sx={{fontSize: 18}} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Grid container spacing={1}>
            {!groupedPermissions ||
            Object.keys(groupedPermissions).length === 0 ? (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1.5,
                  }}
                >
                  <ErrorOutlineIcon
                    color='error'
                    sx={{mr: 0.75, fontSize: 18}}
                  />
                  <Typography variant='body2' sx={{fontWeight: 500}}>
                    {t('dashboardAccountPermissionNoPermission')}
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              Object.keys(groupedPermissions).map(scope => (
                <Grid item key={scope} xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.background.default,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        py: 0.25,
                      }}
                      onClick={() => handleExpandClick(scope)}
                    >
                      <Box
                        sx={{display: 'flex', alignItems: 'center', gap: 0.5}}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            fontSize: '0.8125rem',
                          }}
                        >
                          {t(`scope.${scope}`) || scope}
                        </Typography>
                      </Box>

                      <IconButton
                        aria-expanded={expanded[scope]}
                        aria-label='show more'
                        sx={{
                          p: 0.25,
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        {expanded[scope] ? (
                          <ExpandLessIcon sx={{fontSize: 16}} />
                        ) : (
                          <ExpandMoreIcon sx={{fontSize: 16}} />
                        )}
                      </IconButton>
                    </Box>

                    <Collapse in={expanded[scope]} timeout='auto' unmountOnExit>
                      <Box
                        sx={{
                          mt: 0.75,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.375,
                          '& .MuiChip-root': {
                            height: 20,
                            '& .MuiChip-label': {
                              px: 0.75,
                              fontSize: '0.6875rem',
                            },
                          },
                        }}
                      >
                        {groupedPermissions?.[scope]?.map(
                          (permission, index) => (
                            <Chip
                              key={index}
                              label={`${permission.scope} - ${permission.action}`}
                              color={scopeColors[scope] || 'default'}
                              variant='outlined'
                              sx={{
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            />
                          ),
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default function DashboardUserMe() {
  return <Dashboard element={<AccountPermission />} />;
}
